import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

function buildUnsplashSrcSet(url) {
  const widths = [480, 768, 1024, 1400];
  return widths.map((width) => `${url.replace(/w=\d+/, `w=${width}`)} ${width}w`).join(", ");
}

const heroSlides = [
  {
    title: "Cricket Under Lights",
    subtitle: "Premium turf. Real competition.",
    image:
      "https://images.unsplash.com/photo-1531415074968-036ba1b575da?auto=format&fit=crop&w=1600&q=80"
  },
  {
    title: "Football Match Nights",
    subtitle: "Fast teams. Big goals.",
    image:
      "https://images.unsplash.com/photo-1459865264687-595d652de67e?auto=format&fit=crop&w=1600&q=80"
  },
  {
    title: "Badminton League Weekends",
    subtitle: "Find players and play instantly.",
    image:
      "https://images.unsplash.com/photo-1626224583764-f87db24ac4ea?auto=format&fit=crop&w=1600&q=80"
  }
];

const actionSections = [
  {
    title: "Create a match",
    subtitle: "Pick sport, venue, and time — players discover you instantly.",
    buttonText: "Host a Match",
    path: "/create-match",
    slides: [
      "https://images.unsplash.com/photo-1579952363873-27f3bade9f55?auto=format&fit=crop&w=1400&q=80",
      "https://images.unsplash.com/photo-1593341646782-e0b495cff86d?auto=format&fit=crop&w=1400&q=80"
    ]
  },
  {
    title: "Find your game",
    subtitle: "Browse open matches nearby and jump in with one tap.",
    buttonText: "Join a Match",
    path: "/matches",
    slides: [
      "https://images.unsplash.com/photo-1574629810360-7efbbe195018?auto=format&fit=crop&w=1400&q=80",
      "https://images.unsplash.com/photo-1521412644187-c49fa049e84d?auto=format&fit=crop&w=1400&q=80"
    ]
  },
  {
    title: "Run a tournament",
    subtitle: "Brackets, schedules, and visibility for serious competition.",
    buttonText: "Post a Tournament",
    path: "/create-tournament",
    slides: [
      "https://images.unsplash.com/photo-1546519638-68e109498ffc?auto=format&fit=crop&w=1400&q=80",
      "https://images.unsplash.com/photo-1461896836934-ffe607ba8211?auto=format&fit=crop&w=1400&q=80"
    ]
  },
  {
    title: "Enter the draw",
    subtitle: "Join leagues and tournaments that match your skill level.",
    buttonText: "Join a Tournament",
    path: "/tournaments",
    slides: [
      "https://images.unsplash.com/photo-1517649763962-0c62306601b7?auto=format&fit=crop&w=1400&q=80",
      "https://images.unsplash.com/photo-1530549387789-4c101726664d?auto=format&fit=crop&w=1400&q=80",
      "https://images.unsplash.com/photo-1551958219-acbc608c6377?auto=format&fit=crop&w=1400&q=80",
      "https://images.unsplash.com/photo-1577223625810-445e12aee044?auto=format&fit=crop&w=1400&q=80",
      "https://images.unsplash.com/photo-1612872087720-bb876e2e67d1?auto=format&fit=crop&w=1400&q=80"
    ]
  }
];

function Home({ isLoggedIn }) {
  const navigate = useNavigate();
  const [heroSlide, setHeroSlide] = useState(0);
  const [bannerSlides, setBannerSlides] = useState(() => actionSections.map(() => 0));
  const [motionEnabled, setMotionEnabled] = useState(true);

  useEffect(() => {
    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    const syncMotion = () => setMotionEnabled(!media.matches);
    syncMotion();
    media.addEventListener("change", syncMotion);
    return () => media.removeEventListener("change", syncMotion);
  }, []);

  useEffect(() => {
    if (!motionEnabled) return undefined;

    const timer = setInterval(() => {
      setHeroSlide((prev) => (prev + 1) % heroSlides.length);
    }, 4000);
    return () => clearInterval(timer);
  }, [motionEnabled]);

  useEffect(() => {
    if (!motionEnabled) return undefined;

    const intervalIds = [];
    const timeoutIds = actionSections.map((section, index) =>
      setTimeout(() => {
        intervalIds[index] = setInterval(() => {
          setBannerSlides((prev) => {
            const next = [...prev];
            next[index] = (next[index] + 1) % section.slides.length;
            return next;
          });
        }, 4500);
      }, index * 1200)
    );

    return () => {
      timeoutIds.forEach((id) => clearTimeout(id));
      intervalIds.forEach((id) => {
        if (id) {
          clearInterval(id);
        }
      });
    };
  }, [motionEnabled]);

  const routeAction = (path) => {
    if (!isLoggedIn) {
      navigate("/login");
      return;
    }
    navigate(path);
  };

  return (
    <main className="page home-page">
      <section
        className="hero"
        style={{
          backgroundImage: `linear-gradient(180deg, rgba(255, 255, 255, 0.1), rgba(248, 250, 252, 0.88)), url(${heroSlides[heroSlide].image})`
        }}
      >
        <div className="hero-content">
          <h1>WillPlay</h1>
          <p className="tagline">Host. Join. Compete. Your sports network starts here.</p>
          <h2>{heroSlides[heroSlide].title}</h2>
          <p>{heroSlides[heroSlide].subtitle}</p>
        </div>
      </section>

      <section className="home-chat-strip" aria-label="Chat">
        <div className="home-chat-strip__inner">
          <div>
            <p className="home-chat-strip__title">Messages &amp; chat</p>
            <p className="home-chat-strip__text">
              Open your inbox for support, squad threads, and league updates. Match group chat stays on{" "}
              <Link to="/matches">Find matches</Link> after you join.
            </p>
          </div>
          <Link
            to={isLoggedIn ? "/chat" : "/login"}
            state={isLoggedIn ? undefined : { from: "/chat" }}
            className="primary-btn home-chat-strip__btn"
          >
            {isLoggedIn ? "Open chat" : "Sign in to chat"}
          </Link>
        </div>
      </section>

      <section className="action-stack">
        {actionSections.map((section, index) => {
          const activeSlide = bannerSlides[index] % section.slides.length;
          return (
            <div key={section.buttonText} className="action-banner-wrap">
              <article className="action-banner">
                <div className="action-banner__media" aria-hidden="true">
                  {section.slides.map((slide, slideIndex) => (
                    <img
                      key={slide}
                      className={`action-banner__img${
                        slideIndex === activeSlide ? " is-active" : ""
                      }`}
                      src={slide.replace(/w=\d+/, "w=768")}
                      srcSet={buildUnsplashSrcSet(slide)}
                      sizes="(max-width: 720px) 100vw, (max-width: 1200px) 90vw, 900px"
                      alt=""
                      loading={index === 0 && slideIndex === 0 ? "eager" : "lazy"}
                      decoding="async"
                    />
                  ))}
                  <div className="action-banner__overlay" />
                </div>
                <div className="action-banner__inner">
                  <h3 className="action-banner__title">{section.title}</h3>
                  <p className="action-banner__subtitle">{section.subtitle}</p>
                  {section.slides.length > 1 ? (
                    <div className="action-banner__dots" aria-hidden="true">
                      {section.slides.map((slide, slideIndex) => (
                        <span
                          key={slide}
                          className={`action-banner__dot${
                            slideIndex === activeSlide ? " is-active" : ""
                          }`}
                        />
                      ))}
                    </div>
                  ) : null}
                </div>
              </article>
              <button
                type="button"
                className="primary-btn action-banner__cta"
                onClick={() => routeAction(section.path)}
              >
                {section.buttonText}
              </button>
            </div>
          );
        })}
      </section>

      <footer className="home-footer">
        <p>By using WillPlay, you agree to our platform rules and fair-play policy.</p>
        <div className="home-footer__links">
          <Link to={isLoggedIn ? "/chat" : "/login"} state={isLoggedIn ? undefined : { from: "/chat" }}>
            Chat
          </Link>
          <Link to="/terms">Terms and Conditions</Link>
          <Link to="/privacy">Privacy Policy</Link>
        </div>
      </footer>

    </main>
  );
}

export default Home;